/**
 * XRechnung / ZUGFeRD visualization helpers.
 *
 * Rendering uses the official KoSIT stylesheets
 * (https://github.com/itplr-kosit/xrechnung-visualization), pre-compiled to
 * SaxonJS SEF files that live in /public/xrechnung (see scripts/build-xrechnung-sef.sh).
 * The transformation runs fully in the browser in two steps:
 *   1. UBL Invoice / UBL CreditNote / CII  ->  intermediate "xr" XML
 *   2. intermediate "xr" XML               ->  self-contained HTML
 */
import { pdfjs } from 'react-pdf';

const XRECHNUNG_BASE = '/xrechnung';
const SAXON_SRC = `${XRECHNUNG_BASE}/SaxonJS2.rt.js`;

const STYLESHEETS = {
  'ubl-invoice': 'ubl-invoice-xr.sef.json',
  'ubl-creditnote': 'ubl-creditnote-xr.sef.json',
  cii: 'cii-xr.sef.json',
};
const HTML_STYLESHEET = 'xrechnung-html.sef.json';

// Well-known attachment names for ZUGFeRD / Factur-X / XRechnung PDFs
const EMBEDDED_XML_NAMES = [
  'factur-x.xml',
  'zugferd-invoice.xml',
  'ZUGFeRD-invoice.xml',
  'xrechnung.xml',
  'order-x.xml',
];

export function isXmlFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const name = (file.name || file.title || '').toLowerCase();
  return (
    type === 'text/xml' ||
    type === 'application/xml' ||
    type.endsWith('+xml') ||
    name.endsWith('.xml')
  );
}

export function isPdfFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const name = (file.name || file.title || '').toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
}

/**
 * Detects the syntax of an e-invoice XML.
 * @returns {'ubl-invoice'|'ubl-creditnote'|'cii'|null}
 */
export function detectXRechnungType(xmlString) {
  if (!xmlString) return null;
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
  const root = doc.documentElement;
  if (!root || root.nodeName === 'parsererror') return null;
  const ns = root.namespaceURI || '';
  const local = root.localName;

  if (ns.startsWith('urn:oasis:names:specification:ubl:schema:xsd:Invoice'))
    return 'ubl-invoice';
  if (ns.startsWith('urn:oasis:names:specification:ubl:schema:xsd:CreditNote'))
    return 'ubl-creditnote';
  if (
    local === 'CrossIndustryInvoice' ||
    ns.startsWith('urn:un:unece:uncefact:data:standard:CrossIndustryInvoice')
  )
    return 'cii';
  return null;
}

export function isXRechnungXml(xmlString) {
  return detectXRechnungType(xmlString) !== null;
}

let saxonPromise = null;
function loadSaxon() {
  if (window.SaxonJS) return Promise.resolve(window.SaxonJS);
  if (saxonPromise) return saxonPromise;
  saxonPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SAXON_SRC;
    script.async = true;
    script.onload = () =>
      window.SaxonJS
        ? resolve(window.SaxonJS)
        : reject(new Error('SaxonJS did not initialize'));
    script.onerror = () => {
      saxonPromise = null;
      reject(new Error('Failed to load SaxonJS runtime'));
    };
    document.head.appendChild(script);
  });
  return saxonPromise;
}

/**
 * Renders an XRechnung / ZUGFeRD XML into a self-contained HTML document.
 * @param {string} xmlString
 * @param {{lang?: 'de'|'en'}} options
 * @returns {Promise<string>} HTML markup
 */
export async function renderXRechnungHtml(xmlString, { lang = 'de' } = {}) {
  const type = detectXRechnungType(xmlString);
  if (!type) {
    throw new Error('UNSUPPORTED_XML');
  }
  const SaxonJS = await loadSaxon();
  const base = `${window.location.origin}${XRECHNUNG_BASE}/`;

  const step1 = await SaxonJS.transform(
    {
      stylesheetLocation: `${base}${STYLESHEETS[type]}`,
      sourceText: xmlString,
      destination: 'serialized',
    },
    'async',
  );

  const step2 = await SaxonJS.transform(
    {
      stylesheetLocation: `${base}${HTML_STYLESHEET}`,
      sourceText: step1.principalResult,
      destination: 'serialized',
      stylesheetParams: { lang: lang === 'en' ? 'en' : 'de' },
    },
    'async',
  );

  return step2.principalResult;
}

/**
 * Reads the text content of a file-like object (File/Blob or {path: url}).
 */
export async function readFileText(file) {
  if (!file) return null;
  if (file instanceof Blob) return file.text();
  if (file.path) {
    const res = await fetch(file.path);
    if (!res.ok) throw new Error(`Failed to fetch file (${res.status})`);
    return res.text();
  }
  return null;
}

/**
 * Extracts an embedded e-invoice XML (ZUGFeRD / Factur-X / XRechnung) from a PDF.
 * @param {File|Blob|string} source File object or URL
 * @returns {Promise<{name: string, xml: string}|null>}
 */
export async function extractEmbeddedInvoiceXml(source) {
  if (!source) return null;
  let loadingTask;
  if (source instanceof Blob) {
    const data = new Uint8Array(await source.arrayBuffer());
    loadingTask = pdfjs.getDocument({ data });
  } else {
    loadingTask = pdfjs.getDocument({ url: source });
  }

  const pdf = await loadingTask.promise;
  try {
    const attachments = await pdf.getAttachments();
    if (!attachments) return null;

    const entries = Object.values(attachments).map((a) => ({
      name: a.filename || '',
      content: a.content,
    }));
    if (!entries.length) return null;

    const decode = (bytes) => new TextDecoder('utf-8').decode(bytes);

    // 1) well-known names
    for (const known of EMBEDDED_XML_NAMES) {
      const hit = entries.find(
        (e) => e.name.toLowerCase() === known.toLowerCase(),
      );
      if (hit) {
        const xml = decode(hit.content);
        if (isXRechnungXml(xml)) return { name: hit.name, xml };
      }
    }
    // 2) any *.xml attachment that parses as an e-invoice
    for (const entry of entries) {
      if (!entry.name.toLowerCase().endsWith('.xml')) continue;
      const xml = decode(entry.content);
      if (isXRechnungXml(xml)) return { name: entry.name, xml };
    }
    return null;
  } finally {
    pdf.destroy();
  }
}
